import { Injectable } from '@angular/core';
import { Observable, Subject, Subscriber, Subscription } from 'rxjs';
import Echo, { Channel } from 'laravel-echo';
import { AuthenticationService } from './authentication.service';
import { environment } from '../../../environments/environment';
import { User } from '../../interfaces/routering/user';
import { LocalStorageService } from './local-storage.service';
import Pusher from 'pusher-js';
import { SnackbarService } from './snackbar.service';

@Injectable({
  providedIn: 'root'
})

export class BroadcastService {

  private eventSubjects = new Map<string, Subject<any>>();
  private eventMarkers = new Map<string, boolean>(); // track “stale” flags

  private channelForEventsSubscription: Channel = null;
  private channelForNotificationsSubscription: Channel = null;

  constructor(private authenticationService: AuthenticationService,
              private localStorageService: LocalStorageService,
              private snackbarService: SnackbarService,
  ) {
    this.authenticationService.user$.subscribe((user: User): void => {
      if (user) {
        if (typeof window.Echo !== 'undefined') {
          window.Echo.disconnect();
          window.Echo = undefined;
        }

        if (typeof window.Echo === 'undefined' && typeof Pusher !== 'undefined') {
          window.Echo = new Echo({
            ...environment.pusher,
            ...{
              authEndpoint: environment.api_endpoint + 'broadcasting/auth',
              auth: {
                headers: {
                  Accept: 'application/json',
                  Authorization: `Bearer ${this.localStorageService.get('api-token')}`
                }
              }
            }
          });
        }

        this.subscribeToEvents();
        this.subscribeToNotifications();
      }
    });
  }

  /**
   * Get the event data subject for a specific model and event type.
   *
   * @param modelName
   * @param eventType
   *
   * @returns The BehaviorSubject that holds the event data
   */
  getEventSubject(modelName: string, eventType: string): Observable<any> {
    const eventKey: string = `${modelName}.${eventType}`;

    if (!this.eventSubjects.has(eventKey)) {
      const subject: Subject<any> = new Subject<any>();

      this.eventSubjects.set(eventKey, subject);
    }

    return new Observable<any>((subscriber: Subscriber<any>) => {
      const sub: Subscription = this.eventSubjects.get(eventKey).subscribe(subscriber);

      return (): void => {
        sub.unsubscribe();

        if ((sub as any)._refCount === 0 || !sub['observers'] || sub['observers'].length === 0) {
          this.eventSubjects.get(eventKey)?.complete();
          this.eventSubjects.delete(eventKey);
        }
      };
    });
  }

  /**
   * Clear BehaviorSubject event
   *
   * @param {string} eventName
   */
  clearEventSubject(eventName: string): void {
    if (this.eventSubjects.has(eventName)) {
      this.eventSubjects.get(eventName).complete();
      this.eventSubjects.delete(eventName);
    }
  }

  /**
   * Return if event is stalled and should be refreshed
   *
   * @param {string} eventKey
   * @returns {boolean}
   */
  isStale(eventKey: string): boolean {
    if (this.eventMarkers.has(eventKey)) {
      return this.eventMarkers.get(eventKey);
    }

    return false;
  }

  /**
   * Clear event's stale tracking
   *
   * @param {string} eventKey
   */
  clearStale(eventKey: string): void {
    if (this.eventMarkers.has(eventKey)) {
      this.eventMarkers.delete(eventKey);
    }
  }

  private subscribeToEvents(): void {
    this.authenticationService.user$.subscribe((user: User): void => {
      if (user) {
        const channel: Channel = window.Echo.private('general');

        if (this.channelForEventsSubscription !== null) {
          channel.stopListening('.events');
        }

        this.channelForEventsSubscription = channel.listen('.events', (data: any): void => {
          const eventKey: string = `${data.modelName}.${data.eventType}`;
          if (!environment.production) {
            console.log('WebSocket: received event for ' + eventKey + ', with data:', data);
          }

          if (!this.eventMarkers.has(eventKey)) {
            this.eventMarkers.set(eventKey, true);
          }

          this.eventSubjects.get(eventKey)?.next(data);
        });
      }
    });
  }

  private subscribeToNotifications(): void {
    this.authenticationService.user$.subscribe((user: User): void => {
      if (user) {
        const channel: Channel = window.Echo.private('user.' + user.id);

        if (this.channelForNotificationsSubscription !== null) {
          channel.stopListening('.notifications');
        }

        this.channelForNotificationsSubscription = channel.listen('.notifications', (data: any): void => {
          if (!environment.production) {
            console.log('WebSocket: received notification with data:', data);
          }

          if (data.status) {
            this.snackbarService[data.status](data.message);
          } else {
            this.snackbarService.info(data.message);
          }
        });
      }
    });
  }
}
